import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useState, type FormEvent } from 'react'
import baseUrl from '../../api/baseUrl'
import { ModalOverlay } from '../ModalOverlay'
import LabOrderForm, { type LabOrderPartnerOption } from './LabOrderForm'
import type { LabOrderDetail } from './labOrderTypes'
import {
  type LabOrderFormValues,
  editPayload,
  orderLinesError,
  valuesFromOrder,
} from './labOrderFormUtils'
import { useClinicDentists } from './useClinicDentists'
import { useLabCatalog, useLabPriceList } from './useLabCatalog'

const FORM_ID = 'edit-lab-order-form'

type PartnerRow = {
  id: string
  labId: string | null
  name: string
  isShadow?: boolean
  isOwnLab?: boolean
  address?: string
  phone?: string
}

/**
 * Edit a lab order (requirement 6). The same form as a new order, with the
 * lab and patient fixed and every field the server says is locked disabled
 * (e.g. items and teeth once a registered lab has accepted the order).
 */
export default function EditLabOrderModal({
  order,
  onClose,
  onSaved,
}: {
  order: LabOrderDetail
  onClose: () => void
  onSaved: (order: LabOrderDetail) => void
}) {
  const [values, setValues] = useState<LabOrderFormValues>(() => valuesFromOrder(order))
  const [partners, setPartners] = useState<LabOrderPartnerOption[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const editable = order.editable ?? { fields: [], reason: '' }
  const itemsOpen = editable.fields.includes('invoiceItems')
  const { items: priceList, loading: priceListLoading } = useLabPriceList(values.labId, values.labPartnerId, itemsOpen)
  const { catalog } = useLabCatalog(itemsOpen)
  const dentists = useClinicDentists()

  useEffect(() => {
    let cancelled = false
    baseUrl
      .get('client/labs/partners')
      .then((res) => {
        if (cancelled) return
        const rows: PartnerRow[] = res.data.data ?? []
        setPartners(
          rows.map((p) => ({
            id: String(p.id),
            labId: p.labId ? String(p.labId) : null,
            name: p.name,
            address: p.address,
            phone: p.phone,
            isLocal: p.isShadow,
            isOwnLab: p.isOwnLab,
          })),
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, submitting])

  // The order's own lab must be in the list even if it was removed since.
  const partnerOptions = partners.some((p) => p.id === values.labPartnerId)
    ? partners
    : [{ id: values.labPartnerId, labId: values.labId || null, name: order.labName, isLocal: !values.labId }, ...partners]

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (editable.fields.includes('dueDate') && !values.dueDate) {
      setError('Due date is required')
      return
    }
    if (itemsOpen) {
      const problem = orderLinesError(values.invoiceItems)
      if (problem) {
        setError(problem)
        return
      }
    }
    setSubmitting(true)
    try {
      const res = await baseUrl.patch(
        `client/labs/orders/${encodeURIComponent(order._id)}`,
        editPayload(values, editable.fields),
      )
      onSaved(res.data.data as LabOrderDetail)
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not save the order',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalOverlay
      className="fixed inset-0 z-[210] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-lab-order-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <div
        className="relative flex h-[min(96dvh,920px)] w-full max-w-3xl flex-col rounded-t-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:h-auto sm:max-h-[min(90dvh,880px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3.5 dark:border-slate-700 sm:px-6">
          <div>
            <h2 id="edit-lab-order-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Edit {order.clinicCaseId || `order #${order.id}`}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">{order.labName} · the lab and patient can&apos;t change</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {error ? (
            <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              {error}
            </p>
          ) : null}
          <LabOrderForm
            formId={FORM_ID}
            variant="modal"
            hideActions
            mode="edit"
            editable={editable}
            partners={partnerOptions}
            values={values}
            onChange={setValues}
            onSubmit={submit}
            submitting={submitting}
            submitLabel="Save changes"
            priceListItems={priceList}
            priceListLoading={priceListLoading}
            catalog={catalog}
            dentists={dentists}
          />
        </div>
        <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={FORM_ID}
            disabled={submitting || editable.fields.length === 0}
            className="rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </ModalOverlay>
  )
}
